import React, { useState, useEffect } from 'react';
import { Link, withPrefix } from 'gatsby';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

const logoOrbit = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const sparklePop = keyframes`
  0%   { opacity: 1; transform: scale(0) translate(0, 0); }
  60%  { opacity: 0.8; }
  100% { opacity: 0; transform: scale(1.4) translate(var(--sx), var(--sy)); }
`;
import { navLinks } from '@config';
import { loaderDelay } from '@utils';
import { useScrollDirection, usePrefersReducedMotion } from '@hooks';
import { Menu } from '@components';
import { IconLogo, IconHex } from '@components/icons';

const StyledHeader = styled.header`
  ${({ theme }) => theme.mixins.flexBetween};
  position: fixed;
  top: 0;
  z-index: 11;
  padding: 0px 50px;
  width: 100%;
  height: var(--nav-height);
  background: rgba(7, 6, 13, 0.86);
  border-bottom: 1px solid rgba(201, 162, 39, 0.12);
  filter: none !important;
  pointer-events: auto !important;
  user-select: auto !important;
  backdrop-filter: blur(18px);
  transition: var(--transition);

  @media (max-width: 1080px) {
    padding: 0 40px;
  }
  @media (max-width: 768px) {
    padding: 0 20px;
  }

  @media (prefers-reduced-motion: no-preference) {
    ${props =>
    props.scrollDirection === 'up' &&
      !props.scrolledToTop &&
      css`
        height: var(--nav-scroll-height);
        transform: translateY(0px);
        background: rgba(7, 6, 13, 0.94);
        box-shadow: 0 1px 0 rgba(201, 162, 39, 0.22), 0 14px 34px rgba(0, 0, 0, 0.22);
      `};

    ${props =>
    props.scrollDirection === 'down' &&
      !props.scrolledToTop &&
      css`
        height: var(--nav-scroll-height);
        transform: translateY(calc(var(--nav-scroll-height) * -1));
        box-shadow: 0 1px 0 rgba(201, 162, 39, 0.18);
      `};
  }
`;

const StyledNav = styled.nav`
  ${({ theme }) => theme.mixins.flexBetween};
  position: relative;
  width: 100%;
  color: var(--lightest-slate);
  font-family: var(--font-mono);
  counter-reset: item 0;
  z-index: 12;

  .logo {
    ${({ theme }) => theme.mixins.flexCenter};

    a {
      color: var(--green);
      width: 42px;
      height: 42px;
      position: relative;
      z-index: 1;
      transition: color 0.25s ease, filter 0.25s ease, transform 0.25s ease;

      /* Spinning ceremonial gold arc — Royal Druk orbital glyph */
      &::before {
        content: '';
        position: absolute;
        inset: -7px;
        border-radius: 50%;
        background: conic-gradient(
          from 0deg,
          transparent 0%,
          transparent 50%,
          rgba(201, 162, 39, 0.38) 64%,
          rgba(240, 200, 72, 0.82) 72%,
          rgba(201, 162, 39, 0.38) 80%,
          transparent 94%,
          transparent 100%
        );
        mask: radial-gradient(transparent 44%, black 45%, black 57%, transparent 58%);
        -webkit-mask: radial-gradient(transparent 44%, black 45%, black 57%, transparent 58%);
        z-index: 2;
        pointer-events: none;
        opacity: 0.6;
        transition: opacity 0.3s ease;

        @media (prefers-reduced-motion: no-preference) {
          animation: ${logoOrbit} 7s linear infinite;
        }
      }

      /* Outer static gold ring */
      &::after {
        content: '';
        position: absolute;
        inset: -7px;
        border-radius: 50%;
        border: 1px solid rgba(201, 162, 39, 0.1);
        z-index: 1;
        pointer-events: none;
        transition: border-color 0.3s ease;
      }

      .hex-container {
        position: absolute;
        top: 0;
        left: 0;
        z-index: -1;
        @media (prefers-reduced-motion: no-preference) {
          transition: var(--transition);
        }
      }

      .logo-container {
        position: relative;
        z-index: 1;
        svg {
          fill: none;
          user-select: none;
          @media (prefers-reduced-motion: no-preference) {
            transition: var(--transition);
          }
          polygon {
            fill: var(--dark-navy);
          }
        }
      }

      /* Sparkle symbols that burst on hover */
      .sparkle-burst {
        position: absolute;
        inset: 0;
        pointer-events: none;
        z-index: 3;

        span {
          position: absolute;
          font-size: 9px;
          color: var(--gold-light);
          opacity: 0;
          pointer-events: none;

          &:nth-child(1) {
            top: -4px;
            left: 50%;
            --sx: -6px;
            --sy: -14px;
          }
          &:nth-child(2) {
            top: 50%;
            right: -4px;
            --sx: 14px;
            --sy: -6px;
          }
          &:nth-child(3) {
            bottom: -4px;
            left: 50%;
            --sx: 6px;
            --sy: 14px;
          }
          &:nth-child(4) {
            top: 50%;
            left: -4px;
            --sx: -14px;
            --sy: 6px;
          }
        }
      }

      &:hover,
      &:focus {
        outline: 0;
        color: var(--gold-light);
        filter: drop-shadow(0 0 16px rgba(201, 162, 39, 0.52));
        transform: translateY(-2px);

        &::before {
          opacity: 1;
        }

        &::after {
          border-color: rgba(201, 162, 39, 0.28);
        }

        .hex-container {
          transform: translateY(2px);
        }

        .sparkle-burst span {
          @media (prefers-reduced-motion: no-preference) {
            animation: ${sparklePop} 0.55s ease-out forwards;

            &:nth-child(1) {
              animation-delay: 0s;
            }
            &:nth-child(2) {
              animation-delay: 0.08s;
            }
            &:nth-child(3) {
              animation-delay: 0.16s;
            }
            &:nth-child(4) {
              animation-delay: 0.24s;
            }
          }
        }
      }
    }
  }
`;

const StyledLinks = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }

  ol {
    ${({ theme }) => theme.mixins.flexBetween};
    padding: 0;
    margin: 0;
    list-style: none;

    li {
      margin: 0 5px;
      position: relative;
      counter-increment: item 1;
      font-size: var(--fz-xs);

      a {
        padding: 10px 11px;
        color: rgba(240, 236, 224, 0.7);
        letter-spacing: 0.04em;
        text-transform: uppercase;

        &:before {
          content: '0' counter(item) '.';
          margin-right: 5px;
          color: var(--green);
          font-size: var(--fz-xxs);
          text-align: right;
        }
      }
    }
  }

  .resume-button {
    ${({ theme }) => theme.mixins.smallButton};
    margin-left: 15px;
    font-size: var(--fz-xs);
  }
`;

const Nav = ({ isHome }) => {
  const [isMounted, setIsMounted] = useState(!isHome);
  const scrollDirection = useScrollDirection('down');
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const prefersReducedMotion = usePrefersReducedMotion();

  const handleScroll = () => {
    setScrolledToTop(window.pageYOffset < 50);
  };

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => {
      setIsMounted(true);
    }, 100);

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const timeout = isHome ? loaderDelay : 0;
  const fadeClass = isHome ? 'fade' : '';
  const fadeDownClass = isHome ? 'fadedown' : '';

  const Logo = (
    <div className="logo" tabIndex="-1">
      {isHome ? (
        <a href="/" aria-label="home">
          <div className="hex-container">
            <IconHex />
          </div>
          <div className="logo-container">
            <IconLogo />
          </div>
          <div className="sparkle-burst" aria-hidden="true">
            <span>✦</span>
            <span>✦</span>
            <span>✦</span>
            <span>✦</span>
          </div>
        </a>
      ) : (
        <Link to="/" aria-label="home">
          <div className="hex-container">
            <IconHex />
          </div>
          <div className="logo-container">
            <IconLogo />
          </div>
          <div className="sparkle-burst" aria-hidden="true">
            <span>✦</span>
            <span>✦</span>
            <span>✦</span>
            <span>✦</span>
          </div>
        </Link>
      )}
    </div>
  );

  const ResumeLink = (
    <a
      className="resume-button"
      href={withPrefix('/Wangdi_CV.pdf')}
      target="_blank"
      rel="noopener noreferrer">
      View CV
    </a>
  );

  return (
    <StyledHeader scrollDirection={scrollDirection} scrolledToTop={scrolledToTop}>
      <StyledNav>
        {prefersReducedMotion ? (
          <>
            {Logo}

            <StyledLinks>
              <ol>
                {navLinks &&
                  navLinks.map(({ url, name }, i) => (
                    <li key={i}>
                      <Link to={url}>{name}</Link>
                    </li>
                  ))}
              </ol>
              <div>{ResumeLink}</div>
            </StyledLinks>

            <Menu />
          </>
        ) : (
          <>
            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout}>
                  <>{Logo}</>
                </CSSTransition>
              )}
            </TransitionGroup>

            <StyledLinks>
              <ol>
                <TransitionGroup component={null}>
                  {isMounted &&
                    navLinks &&
                    navLinks.map(({ url, name }, i) => (
                      <CSSTransition key={i} classNames={fadeDownClass} timeout={timeout}>
                        <li key={i} style={{ transitionDelay: `${isHome ? i * 100 : 0}ms` }}>
                          <Link to={url}>{name}</Link>
                        </li>
                      </CSSTransition>
                    ))}
                </TransitionGroup>
              </ol>

              <TransitionGroup component={null}>
                {isMounted && (
                  <CSSTransition classNames={fadeDownClass} timeout={timeout}>
                    <div style={{ transitionDelay: `${isHome ? navLinks.length * 100 : 0}ms` }}>
                      {ResumeLink}
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>
            </StyledLinks>

            <TransitionGroup component={null}>
              {isMounted && (
                <CSSTransition classNames={fadeClass} timeout={timeout}>
                  <Menu />
                </CSSTransition>
              )}
            </TransitionGroup>
          </>
        )}
      </StyledNav>
    </StyledHeader>
  );
};

Nav.propTypes = {
  isHome: PropTypes.bool,
};

export default Nav;
